import React, { useState } from 'lib/react';
import {
  EVButton,
  EVGrid, EVPaper, EVTypography, makeStyles,
  EVKeyboardArrowRight, EVKeyboardArrowUp, EVKeyboardArrowDown,
} from 'lib/components';
import { useLocation, Link } from 'lib/router';
import { useTranslation } from 'lib/translation';
import PropTypes from 'prop-types';
import clsx from 'lib/clsx';
import { REPORT_DETAIL_ROUTE } from 'constants/RouteConstants';
import Typography from 'commonComponents/typography';
import { hasExpandableContent } from 'utils/app.utils';

const useStyles = makeStyles((theme) => ({
  reportsList: {
    backgroundColor: theme.evPalette.charcoal[100],
    padding: '8px',
    rowGap: '8px',
  },
  reportListItemPaper: {
    position: 'relative',
    boxShadow: 'none',
    outline: `1px solid ${theme.evPalette.charcoal[400]}`,
    outlineOffset: '-1px',
    '&.border': {
      outline: `1px solid ${theme.evPalette.ocean[600]}`,
    },
  },
  reportListItem: {
    position: 'relative',
    padding: '16px',
    rowGap: '8px',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    textDecoration: 'none',
    color: 'inherit',
    '&>div': {
      '&:first-child': { fontWeight: 600 },
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '18px',
      color: theme.evPalette.charcoal[900],
    },
  },
  reportDetailRow: {
    columnGap: '16px',
    '&>div>p': {
      color: theme.evPalette.charcoal[800],
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '24px',
      '&:first-child': {
        textTransform: 'uppercase',
        fontWeight: 600,
        fontSize: '12px',
        lineHeight: '18px',
        color: theme.evPalette.charcoal[900],
      },
    },
  },
  actions: {
    gap: '8px',
    '&>*': {
      width: 'unset',
      flex: '1 0 40%',
      justifyContent: 'center',
    },
  },
  detailsBtn: {
    position: 'absolute',
    right: 0,
    top: 0,
    boxSizing: 'border-box',
    height: '100%',
    minWidth: '44px',
    width: '44px',
    borderRight: '5px solid',
    borderRadius: '4px',
    padding: 0,
    '& .myev-MuiButton-label': {
      position: 'absolute !important',
      top: '50% !important',
    },
  },
  expandBtn: {
    padding: '10px 16px',
    cursor: 'pointer',
  },
  expandBtnLabel: {
    display: 'flex',
    gap: '8px',
  },
  expandedRow: {
    display: 'flex',
    width: 'fit-content',
    gap: '24px',
    paddingLeft: '8px',
    flexWrap: 'wrap',
    padding: '0px 16px',
    maxHeight: 0,
    overflow: 'hidden',
    transition: 'padding 0.5s ease-out, max-height 0.5s ease-out',
    '&.expanded': {
      padding: '16px',
      maxHeight: '2000px',
    },
  },
}));

/** Component to render the Report details in list format for Order History Page in mobile view (Subscription variant) */
function ReportsListSubscription({
  reportDetailsList, ExpandedRowComponent, expandedRowFlags, onExpandedRowFlagsChange,
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const classes = useStyles();

  const currentUrl = `${location.pathname}${location.search}`;

  const [expandedRowFlagsState, setExpandedRowFlags] = useState({});

  const handleExpandedRowVisibility = (key) => {
    if (onExpandedRowFlagsChange) return onExpandedRowFlagsChange(key);
    return setExpandedRowFlags((prevState) => ({ ...prevState, [key]: !prevState[key] }));
  };
  const expandedRows = expandedRowFlags || expandedRowFlagsState;

  return (
    <EVGrid className={classes.reportsList} container direction="column">
      {(reportDetailsList?.length > 0) ? reportDetailsList.map(({
        rowKey,
        productAndAddress, statusAndActions,
        reportIdCell, claimNumber, date, delivery,
        subscriptionDetails,
        reportId, expandedRow, isLoading,
      }) => {
        const statusChip = statusAndActions?.props?.children?.[0];
        const actionsFragment = statusAndActions?.props?.children?.[1];
        const [detailsButton, ...restActions] = actionsFragment?.props?.children || [];

        return (
          <EVPaper
            key={rowKey}
            className={clsx(classes.reportListItemPaper, detailsButton && 'border')}
          >
            <EVGrid
              className={classes.reportListItem}
              component={Link}
              to={detailsButton && {
                pathname: REPORT_DETAIL_ROUTE.replace(':reportId', reportId),
                state: { currentUrl },
              }}
            >
              {/* Product name and Status chip */}
              <EVGrid container justifyContent="space-between" alignItems="flex-start">
                <EVGrid style={{ flex: 1 }}>
                  {productAndAddress}
                </EVGrid>
                <EVGrid style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  alignItems: 'flex-end',
                  justifyContent: 'flex-end',
                }}
                >
                  {statusChip}
                  {subscriptionDetails}
                </EVGrid>
              </EVGrid>

              {/* Report ID and Claim ID row */}
              <EVGrid container wrap="nowrap" className={classes.reportDetailRow}>
                <EVGrid style={{ flex: 1 }}>
                  {!isLoading && <EVTypography>{t('order_history__report_id')}</EVTypography>}
                  <EVTypography>{reportIdCell}</EVTypography>
                </EVGrid>
                <EVGrid style={{ flex: 1 }}>
                  {!isLoading && <EVTypography>{t('common__claim_id')}</EVTypography>}
                  <EVTypography>{claimNumber}</EVTypography>
                </EVGrid>
              </EVGrid>

              {/* Date Ordered and Delivery row */}
              <EVGrid container wrap="nowrap" className={classes.reportDetailRow}>
                <EVGrid style={{ flex: 1 }}>
                  {!isLoading && <EVTypography>{t('order_history__date_ordered')}</EVTypography>}
                  <EVTypography>{date}</EVTypography>
                </EVGrid>
                <EVGrid style={{ flex: 1 }}>
                  {!isLoading && <EVTypography>{t('common_delivery')}</EVTypography>}
                  <EVTypography>{delivery}</EVTypography>
                </EVGrid>
              </EVGrid>

              {/* Action buttons */}
              {restActions && restActions.length > 0 && (
                <EVGrid container className={classes.actions}>
                  {restActions}
                </EVGrid>
              )}

              {detailsButton && (
                <EVButton
                  id={`detailsBtn-${reportId}`}
                  className={classes.detailsBtn}
                  color="primary"
                >
                  <EVKeyboardArrowRight />
                </EVButton>
              )}
            </EVGrid>

            {/* More details section */}
            <EVGrid style={{ display: 'flex', flexDirection: 'column', backgroundColor: '#F6F7FF' }}>
              {hasExpandableContent(expandedRow) && ExpandedRowComponent && (
                <EVButton
                  id={`expand-btn-${rowKey}`}
                  className={classes.expandBtn}
                  onClick={() => handleExpandedRowVisibility(rowKey)}
                >
                  <Typography variant="body-500" fontColor="ocean-600" className={classes.expandBtnLabel}>
                    {expandedRows[rowKey] ? t('order_history__less_details') : t('order_history__more_details')}
                    {expandedRows[rowKey] ? <EVKeyboardArrowUp /> : <EVKeyboardArrowDown />}
                  </Typography>
                </EVButton>
              )}
              <EVGrid className={classes.reportDetailRow}>
                <EVGrid className={clsx(
                  classes.expandedRow,
                  expandedRows[rowKey] && hasExpandableContent(expandedRow) && 'expanded',
                )}
                >
                  {expandedRows[rowKey] && hasExpandableContent(expandedRow) && <ExpandedRowComponent expandedRowData={expandedRow} />}
                </EVGrid>
              </EVGrid>
            </EVGrid>
          </EVPaper>
        );
      }) : <EVGrid>No results</EVGrid>}
    </EVGrid>
  );
}

ReportsListSubscription.defaultProps = {
  ExpandedRowComponent: null,
  expandedRowFlags: null,
  onExpandedRowFlagsChange: null,
};
ReportsListSubscription.propTypes = {
  reportDetailsList: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  ExpandedRowComponent: PropTypes.func,
  expandedRowFlags: PropTypes.arrayOf(PropTypes.bool),
  onExpandedRowFlagsChange: PropTypes.func,
};

export default ReportsListSubscription;
